import { __assign, __awaiter, __generator } from "tslib";
import { useConst } from '@fluentui/react-hooks';
import { AppbarConnectorService } from '@office-start/appbar-connector-service';
import { FileType } from '@office-start/document-types';
import { fetchFeatureGates, getFeatureGates, } from '@office-start/feature-gates-service';
import { fetchFeatureSettings, getFeatureSettings, } from '@office-start/feature-settings-service';
import { FluidQueryParams } from '@office-start/fluid-types';
import { isExtendedRouteItem, isMosRouteItem, MosAppRoutes, OfficeHomeQueryParams, } from '@office-start/officehome-types';
import { tryGetAppHostedAtRoot, useCurrentRoute, } from '@office-start/route-utility';
import { TemplatesQueryParams } from '@office-start/template-types';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { generatePath, useHistory, useParams, } from 'react-router-dom';
import { BehaviorSubject } from 'rxjs';
import { getOfficeRouteItemFromRoute } from './getOfficeRouteItemFromRoute';
import { AppGalleryQueryParams } from './models/AppGalleryQueryParams';
import { MosAppQueryParams } from './models/MosAppQueryParams';
import { SearchQueryParams } from './models/SearchQueryParams';
import { removeQueryParamsFromUrl } from './utils';
export function getRouteItemFromPathMatch(routeToPath, mosAppsHostedAtRoot, pathMatch) {
    if (!pathMatch) {
        return undefined;
    }
    // Handle app hosted at root route
    // appId in pathMatch is always friendly app id
    var guidMosAppId = tryGetAppHostedAtRoot(pathMatch.url, mosAppsHostedAtRoot);
    if (guidMosAppId) {
        return getOfficeRouteItemFromRoute('M365Apps', __assign(__assign({}, pathMatch.params), { appId: guidMosAppId }));
    }
    // Get route name from path match
    // ie if path is /m365apps/:appId/:entityId?/:subPageId?, route name is M365Apps
    for (var routeName in routeToPath) {
        if (routeToPath[routeName] === pathMatch.path) {
            return getOfficeRouteItemFromRoute(routeName, pathMatch.params);
        }
    }
    return undefined;
}
// eslint-disable-next-line max-params
export function getPathForRouteItem(routeItem, routeToPath, currentSearchString, additionalParams, pathOverride) {
    var path = pathOverride !== null && pathOverride !== void 0 ? pathOverride : routeToPath[routeItem.route];
    if (!path) {
        return;
    }
    var routeParams = {};
    if (routeItem.app) {
        routeParams.app = routeItem.app;
    }
    // $TODO: There's got to be a way to leverage react router to do the path matching
    // instead of having to do this manually with the type guard.
    if (isMosRouteItem(routeItem)) {
        routeItem.appId && (routeParams.appId = routeItem.appId);
        routeItem.entityId && (routeParams.entityId = routeItem.entityId);
        if (routeItem.subPageId) {
            routeParams.subPageId = routeItem.subPageId;
        }
        else if (currentSearchString &&
            currentSearchString.indexOf('subPageId') > -1) {
            var subPageId = new URLSearchParams(currentSearchString).get('subPageId');
            subPageId && (routeParams.subPageId = subPageId);
        }
    }
    else if (isExtendedRouteItem(routeItem)) {
        routeParams.entityId = routeItem.entityId;
        routeParams.subEntityId = routeItem.subEntityId;
    }
    var pathWithQueryParams = path;
    if (currentSearchString || additionalParams) {
        var queryParams_1 = currentSearchString
            ? new URLSearchParams(currentSearchString)
            : new URLSearchParams();
        if (routeItem.route !== 'Search') {
            queryParams_1 = removeQueryParamsFromUrl(queryParams_1, SearchQueryParams);
        }
        if (routeItem.route !== 'AppGallery') {
            queryParams_1 = removeQueryParamsFromUrl(queryParams_1, AppGalleryQueryParams);
        }
        if (!(routeItem.route === 'Content' && routeItem.app === 'Fluid')) {
            queryParams_1 = removeQueryParamsFromUrl(queryParams_1, FluidQueryParams);
        }
        if (routeItem.route !== 'Templates') {
            queryParams_1 = removeQueryParamsFromUrl(queryParams_1, TemplatesQueryParams);
        }
        if (routeItem.route === 'OfficeHome') {
            queryParams_1 = removeQueryParamsFromUrl(queryParams_1, OfficeHomeQueryParams);
        }
        queryParams_1 = removeQueryParamsFromUrl(queryParams_1, MosAppQueryParams);
        //Append additionalParams value to query parameters
        if (additionalParams) {
            Object.keys(additionalParams).map(function (key) {
                var _a;
                //value needs to be string for URLSearchParams
                //https://github.com/microsoft/TypeScript/issues/32951
                var value = (_a = additionalParams[key]) === null || _a === void 0 ? void 0 : _a.toString();
                if (!value && queryParams_1.has(key)) {
                    queryParams_1.delete(key);
                }
                value && queryParams_1.set(key, value);
            });
        }
        pathWithQueryParams += "?".concat(queryParams_1.toString());
    }
    if (routeItem.route === 'Search') {
        return pathWithQueryParams;
    }
    return generatePath(pathWithQueryParams, routeParams);
}
// Get mutable refs and const observable for use by router callbacks
function useOfficeExperienceRouterRefs(routes, routeToPath, mosAppsHostedAtRoot) {
    var params = useParams();
    var paramsRef = useRef(params);
    useEffect(function () {
        paramsRef.current = params;
    }, [params]);
    var matchedPath = useCurrentRoute(routes);
    var currentRoute = useMemo(function () {
        var routeItem = getRouteItemFromPathMatch(routeToPath, mosAppsHostedAtRoot, matchedPath);
        return routeItem;
    }, [mosAppsHostedAtRoot, matchedPath, routeToPath]);
    var routeRef = useRef(currentRoute);
    var routeSubject = useConst(new BehaviorSubject(currentRoute));
    var routeObservable = useMemo(function () { return routeSubject.asObservable(); }, [routeSubject]);
    useEffect(function () {
        routeRef.current = currentRoute;
        routeSubject.next(currentRoute);
    }, [currentRoute, routeSubject]);
    return { routeRef: routeRef, paramsRef: paramsRef, routeObservable: routeObservable };
}
// NOTE: The useRefs in this hook are a hacky way to provide non-stale callbacks to the router, which is only added to context once.
// TODO: Move routing methods to separate hooks that are detached from context.
export function useOfficeExperienceRouter(routes, routeToPath, mosAppsHostedAtRoot, openHostedDocument) {
    var _this = this;
    var history = useHistory();
    // Listener for browser back and forward button press
    useEffect(function () {
        var unlisten = history.listen(function (location, action) {
            if (action === 'POP') {
                AppbarConnectorService.getInstance().onAppButtonClick(Date.now(), true);
            }
        });
        return function () {
            unlisten();
        };
    }, [history]);
    var _a = useOfficeExperienceRouterRefs(routes, routeToPath, mosAppsHostedAtRoot), routeRef = _a.routeRef, paramsRef = _a.paramsRef, routeObservable = _a.routeObservable;
    // Use props and refs to generate router callbacks
    // NOTE: The component context and router are currently only assigned once during app lifetime,
    // so any rerender of this hook will not update the router callbacks used by the app.
    // Router callbacks dependency arrays should therefore not include values that are apt to change,
    // since any recreation of the callbacks will not be picked up by the context.
    // This is temporary until these callbacks are moved to hooks that don't rely on component context.
    var getCurrentRoute = useCallback(function () {
        return routeRef.current;
    }, [routeRef]);
    var getCurrentRouteQueryParams = useCallback(function () {
        return paramsRef.current;
    }, [paramsRef]);
    var getCurrentPageForLogging = useCallback(function () {
        var route = routeRef.current;
        if (route && route.appId !== undefined) {
            return route
                ? "".concat(route.appId).concat(route.route)
                : 'Unknown';
        }
        return route
            ? "".concat(route.app ? FileType[route.app] : '').concat(route.route)
            : 'Unknown';
    }, [routeRef]);
    var getRouteObservable = useCallback(function () { return routeObservable; }, [routeObservable]);
    var getRoutePathWithParams = useCallback(function (route, additionalParams, featureGates, featureSettngs) {
        var _a, _b, _c, _d;
        switch (route.route) {
            case 'M365Apps':
                var mosRoute = route;
                var pathOverride = undefined;
                var friendlyAppId = undefined;
                if (mosRoute.appId &&
                    ((_a = featureGates === null || featureGates === void 0 ? void 0 : featureGates.mosAppHostedAtRootGates) === null || _a === void 0 ? void 0 : _a.mosAppHostedAtRootEnabled)) {
                    friendlyAppId = tryGetAppHostedAtRoot(mosRoute.appId, (_c = (_b = featureSettngs === null || featureSettngs === void 0 ? void 0 : featureSettngs.routing) === null || _b === void 0 ? void 0 : _b.mosAppsHostedAtRoot) === null || _c === void 0 ? void 0 : _c.split(','), true);
                    if (friendlyAppId) {
                        // If the mos app should be hosted at root, remove /m365apps from path and replace guild app Id in url with friendly app Id
                        pathOverride = (_d = routeToPath['M365Apps']) === null || _d === void 0 ? void 0 : _d.replace('/m365apps', '');
                    }
                }
                return getPathForRouteItem(__assign(__assign({}, route), { appId: friendlyAppId !== null && friendlyAppId !== void 0 ? friendlyAppId : mosRoute.appId }), routeToPath, history.location.search, additionalParams, pathOverride);
            // TODO: add start page handling
            default:
                return getPathForRouteItem(route, routeToPath, history.location.search, additionalParams);
        }
    }, [history.location.search, routeToPath]);
    var getSearchParams = useCallback(function () {
        var urlParams = history.location.search;
        return urlParams
            ? new URLSearchParams(urlParams)
            : new URLSearchParams();
    }, [history.location.search]);
    var navigateTo = useCallback(function (route, replace, additionalParams) { return __awaiter(_this, void 0, void 0, function () {
        var featureGatesResult, featureGates, _a, featureSettingsResult, featureSettings, _b, path, isSamePath, currentPathWithSearch, isM365AppsRoute;
        return __generator(this, function (_c) {
            switch (_c.label) {
                case 0:
                    featureGatesResult = getFeatureGates();
                    if (!(featureGatesResult === null || featureGatesResult === void 0 ? void 0 : featureGatesResult.result)) return [3 /*break*/, 1];
                    _a = featureGatesResult;
                    return [3 /*break*/, 3];
                case 1: return [4 /*yield*/, fetchFeatureGates()];
                case 2:
                    _a = _c.sent();
                    _c.label = 3;
                case 3:
                    featureGates = _a;
                    featureSettingsResult = getFeatureSettings();
                    if (!(featureSettingsResult === null || featureSettingsResult === void 0 ? void 0 : featureSettingsResult.result)) return [3 /*break*/, 4];
                    _b = featureSettingsResult;
                    return [3 /*break*/, 6];
                case 4: return [4 /*yield*/, fetchFeatureSettings()];
                case 5:
                    _b = _c.sent();
                    _c.label = 6;
                case 6:
                    featureSettings = _b;
                    path = getRoutePathWithParams(route, additionalParams, featureGates === null || featureGates === void 0 ? void 0 : featureGates.result, featureSettings === null || featureSettings === void 0 ? void 0 : featureSettings.result);
                    if (!path) {
                        return [2 /*return*/, false];
                    }
                    try {
                        currentPathWithSearch = "".concat(decodeURIComponent(location.pathname)).concat(decodeURIComponent(location.search)).toLowerCase();
                        isSamePath = currentPathWithSearch === null || currentPathWithSearch === void 0 ? void 0 : currentPathWithSearch.includes(decodeURIComponent(path).toLowerCase());
                    }
                    catch (error) {
                        isSamePath = false;
                    }
                    isM365AppsRoute = MosAppRoutes.includes(route.route);
                    if (replace) {
                        history.replace(path);
                    }
                    else if (!isM365AppsRoute || !isSamePath) {
                        history.push(path);
                    }
                    return [2 /*return*/, true];
            }
        });
    }); }, [getRoutePathWithParams, history]);
    var navigateBack = useCallback(function () {
        history.goBack();
    }, [history]);
    var router = useMemo(function () {
        return {
            getCurrentRoute: getCurrentRoute,
            getCurrentRouteQueryParams: getCurrentRouteQueryParams,
            getCurrentPageForLogging: getCurrentPageForLogging,
            getRouteObservable: getRouteObservable,
            getSearchParams: getSearchParams,
            getUrlForRoute: getRoutePathWithParams,
            navigateBack: navigateBack,
            navigateTo: navigateTo,
            openHostedDocument: openHostedDocument,
        };
    }, [
        getCurrentRoute,
        getCurrentRouteQueryParams,
        getCurrentPageForLogging,
        getRouteObservable,
        getRoutePathWithParams,
        getSearchParams,
        navigateBack,
        navigateTo,
        openHostedDocument,
    ]);
    return router;
}
//# sourceMappingURL=useOfficeExperienceRouter.js.map